import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import ExportToolbar from "../components/ExportToolbar";
import {
  DollarSign, Loader2, BarChart3, TrendingUp, TrendingDown, AlertTriangle,
  CheckCircle, Target, ArrowUp, ArrowDown, Minus, Lightbulb, FileCheck, Activity
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) => {
  if (!v && v !== 0) return "$0";
  const abs = Math.abs(v);
  if (abs >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};

const MetricCard = ({ label, value, format = "number", color = "", subtext = "", testId = "" }) => {
  const display = format === "money" ? fmt(value) : format === "percent" ? `${(value || 0).toFixed(2)}%` : format === "index" ? (value || 0).toFixed(3) : value;
  return (
    <div className="p-4 bg-white border-2 border-zinc-200 text-center" data-testid={testId}>
      <p className="text-xs font-bold uppercase text-zinc-500 mb-1">{label}</p>
      <p className={`text-2xl font-black ${color}`}>{display}</p>
      {subtext && <p className="text-xs text-zinc-400 mt-1">{subtext}</p>}
    </div>
  );
};

const HealthBadge = ({ status }) => {
  const c = { excellent: "bg-emerald-100 text-emerald-700", good: "bg-green-100 text-green-700", fair: "bg-amber-100 text-amber-700", poor: "bg-orange-100 text-orange-700", critical: "bg-red-100 text-red-700" };
  return <Badge className={`${c[status] || c.fair} border-none text-sm px-3 py-1`}>{status?.toUpperCase()}</Badge>;
};

// S-Curve Chart Component (Canvas-based)
const SCurveChart = ({ pv, ev, ac, bac, title = "S-Curve Analysis" }) => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width = canvas.offsetWidth * 2;
    const h = canvas.height = canvas.offsetHeight * 2;
    ctx.scale(2, 2);
    const cw = w / 2, ch = h / 2;
    const pad = { t: 30, r: 20, b: 40, l: 60 };
    const pw = cw - pad.l - pad.r, ph = ch - pad.t - pad.b;

    ctx.clearRect(0, 0, cw, ch);
    ctx.fillStyle = '#fafafa';
    ctx.fillRect(0, 0, cw, ch);

    // Grid
    ctx.strokeStyle = '#e4e4e7';
    ctx.lineWidth = 0.5;
    for (let i = 0; i <= 5; i++) {
      const y = pad.t + (ph / 5) * i;
      ctx.beginPath(); ctx.moveTo(pad.l, y); ctx.lineTo(pad.l + pw, y); ctx.stroke();
      ctx.fillStyle = '#71717a'; ctx.font = '10px monospace'; ctx.textAlign = 'right';
      ctx.fillText(fmt((bac || 100) * (1 - i / 5)), pad.l - 5, y + 3);
    }

    const maxVal = Math.max(pv, ev, ac, bac) * 1.1 || 100;
    const months = 12;
    
    // Generate S-curve data points
    const genSCurve = (total, variance = 0) => {
      const pts = [];
      for (let i = 0; i <= months; i++) {
        const t = i / months;
        const s = 1 / (1 + Math.exp(-10 * (t - 0.5))); // Sigmoid
        pts.push(total * s * (1 + variance * (Math.random() - 0.5) * 0.1));
      }
      return pts;
    };

    const pvPts = genSCurve(bac || pv * 2);
    const evPts = genSCurve(ev, 0.05);
    const acPts = genSCurve(ac, 0.08);

    const drawLine = (pts, color, label) => {
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.beginPath();
      pts.forEach((v, i) => {
        const x = pad.l + (pw / months) * i;
        const y = pad.t + ph - (v / maxVal) * ph;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      });
      ctx.stroke();
    };

    drawLine(pvPts, '#0047AB', 'PV');
    drawLine(evPts, '#10b981', 'EV');
    drawLine(acPts, '#FF4F00', 'AC');

    // Legend
    const legend = [{ c: '#0047AB', l: 'Planned Value (PV)' }, { c: '#10b981', l: 'Earned Value (EV)' }, { c: '#FF4F00', l: 'Actual Cost (AC)' }];
    legend.forEach((item, i) => {
      const x = pad.l + i * 150;
      ctx.fillStyle = item.c;
      ctx.fillRect(x, ch - 15, 20, 3);
      ctx.fillStyle = '#71717a'; ctx.font = '9px sans-serif'; ctx.textAlign = 'left';
      ctx.fillText(item.l, x + 25, ch - 11);
    });

    // Title
    ctx.fillStyle = '#18181b'; ctx.font = 'bold 11px sans-serif'; ctx.textAlign = 'left';
    ctx.fillText(title, pad.l, 15);
  }, [pv, ev, ac, bac, title]);

  return <canvas ref={canvasRef} className="w-full h-[280px] border border-zinc-200" style={{ imageRendering: 'auto' }} />;
};

// EV Gauge Component
const EVGauge = ({ value, label, target = 1, color }) => {
  const pct = Math.min(Math.max((value / target) * 100, 0), 200);
  const isGood = value >= target;
  const gaugeColor = color || (isGood ? '#10b981' : '#FF4F00');
  return (
    <div className="text-center p-4 bg-white border-2 border-zinc-200">
      <div className="relative w-24 h-24 mx-auto mb-2">
        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
          <circle cx="50" cy="50" r="42" fill="none" stroke="#e4e4e7" strokeWidth="8" />
          <circle cx="50" cy="50" r="42" fill="none" stroke={gaugeColor} strokeWidth="8"
            strokeDasharray={`${Math.min(pct, 100) * 2.64} 264`} strokeLinecap="round" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-lg font-black" style={{ color: gaugeColor }}>{value?.toFixed(2)}</span>
        </div>
      </div>
      <p className="text-xs font-bold uppercase text-zinc-500">{label}</p>
      <p className="text-[10px] text-zinc-400">Target: {target}</p>
    </div>
  );
};

const FinancialSummaryView = ({ contracts }) => {
  const [loading, setLoading] = useState(false);
  const [summaryResult, setSummaryResult] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [activeTab, setActiveTab] = useState("input");
  const [selectedContract, setSelectedContract] = useState("");
  const [form, setForm] = useState({
    contract_id: "", original_contract_value: "", budget_at_completion: "",
    planned_value: "", earned_value: "", actual_cost: "",
    approved_changes_value: "", pending_changes_value: "",
    retention_percent: "5", advance_payment_percent: "10",
    payments_certified: "", payments_received: "",
    percent_complete: "", months_elapsed: "", total_months: ""
  });

  const handleContractSelect = (contractId) => {
    setSelectedContract(contractId);
    if (contractId) {
      const c = (contracts || []).find(x => x.id === contractId);
      if (c) {
        setForm(p => ({ ...p, contract_id: contractId, original_contract_value: c.contract_value?.toString() || "", total_months: c.duration_months?.toString() || "" }));
      }
    }
  };

  const handleSubmit = async () => {
    if (!form.original_contract_value || !form.planned_value || !form.earned_value || !form.actual_cost) {
      toast.error("Please fill in core financial values (OCV, PV, EV, AC)");
      return;
    }
    setLoading(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => { payload[k] = v ? (isNaN(v) ? v : parseFloat(v)) : 0; });
      if (!payload.contract_id) payload.contract_id = "manual";
      const res = await axios.post(`${API}/financial/summary`, payload);
      setSummaryResult(res.data);
      setActiveTab("dashboard");
      toast.success("Financial summary generated!");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const handleAIAnalysis = async () => {
    if (!summaryResult) return;
    setLoading(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => { payload[k] = v ? (isNaN(v) ? v : parseFloat(v)) : 0; });
      if (!payload.contract_id) payload.contract_id = "manual";
      const res = await axios.post(`${API}/financial/ai-analysis`, payload);
      setAiAnalysis(res.data);
      toast.success("AI analysis complete!");
    } catch (e) { toast.error(e.response?.data?.detail || "AI analysis failed"); }
    finally { setLoading(false); }
  };

  const InputField = ({ label, field, placeholder = "" }) => (
    <div>
      <label className="text-xs font-bold uppercase text-zinc-500 block mb-1">{label}</label>
      <input type="text" inputMode="decimal" value={form[field]} onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))} className="w-full border-2 border-zinc-200 p-2 text-sm focus:border-black focus:outline-none transition-colors" placeholder={placeholder} data-testid={`fin-${field}`} />
    </div>
  );

  const ev = summaryResult?.ev_analysis;
  const cs = summaryResult?.cash_summary;
  const fc = summaryResult?.forecasting;

  return (
    <div className="space-y-6" data-testid="financial-summary-view" data-export-region>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight uppercase font-['Chivo'] mb-2">Financial Summary</h1>
          <p className="text-zinc-500">Earned Value analysis, S-Curves, cost forecasting, and financial dashboards</p>
        </div>
        <ExportToolbar title="Financial Summary" pdfFilename="financial-summary" />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-zinc-100 p-1">
          <TabsTrigger value="input"><DollarSign size={14} className="mr-1" /> Data Input</TabsTrigger>
          <TabsTrigger value="dashboard"><BarChart3 size={14} className="mr-1" /> Dashboard</TabsTrigger>
          <TabsTrigger value="scurves"><Activity size={14} className="mr-1" /> S-Curves</TabsTrigger>
          <TabsTrigger value="forecasting"><TrendingUp size={14} className="mr-1" /> Forecasting</TabsTrigger>
        </TabsList>

        <TabsContent value="input">
          <div className="space-y-6">
            {/* Contract Selector */}
            {contracts?.length > 0 && (
              <div className="border border-zinc-200 bg-white p-4">
                <label className="text-xs font-bold uppercase text-zinc-500 block mb-2">Pre-fill from Contract</label>
                <select value={selectedContract} onChange={e => handleContractSelect(e.target.value)} className="w-full border-2 border-zinc-200 p-2 text-sm" data-testid="fin-contract-select">
                  <option value="">Manual entry...</option>
                  {contracts.map(c => (<option key={c.id} value={c.id}>{c.project_name || c.name} ({c.framework})</option>))}
                </select>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border-2 border-black brutal-shadow bg-white p-6">
                <h3 className="font-bold font-['Chivo'] uppercase mb-4 pb-3 border-b border-zinc-200">Contract Values</h3>
                <div className="space-y-4">
                  <InputField label="Original Contract Value ($) *" field="original_contract_value" placeholder="50000000" />
                  <InputField label="Budget at Completion ($)" field="budget_at_completion" placeholder="52000000" />
                  <InputField label="Approved Changes ($)" field="approved_changes_value" placeholder="2000000" />
                  <InputField label="Pending Changes ($)" field="pending_changes_value" placeholder="1500000" />
                </div>
              </div>

              <div className="border-2 border-black brutal-shadow bg-white p-6">
                <h3 className="font-bold font-['Chivo'] uppercase mb-4 pb-3 border-b border-zinc-200">Earned Value Data</h3>
                <div className="space-y-4">
                  <InputField label="Planned Value (PV/BCWS) $ *" field="planned_value" placeholder="25000000" />
                  <InputField label="Earned Value (EV/BCWP) $ *" field="earned_value" placeholder="23000000" />
                  <InputField label="Actual Cost (AC/ACWP) $ *" field="actual_cost" placeholder="24000000" />
                  <InputField label="% Complete" field="percent_complete" placeholder="45" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border border-zinc-200 bg-white p-6">
                <h3 className="font-bold text-sm uppercase mb-4">Payment Info</h3>
                <div className="space-y-4">
                  <InputField label="Payments Certified ($)" field="payments_certified" placeholder="20000000" />
                  <InputField label="Payments Received ($)" field="payments_received" placeholder="18000000" />
                  <div className="grid grid-cols-2 gap-4">
                    <InputField label="Retention %" field="retention_percent" placeholder="5" />
                    <InputField label="Advance %" field="advance_payment_percent" placeholder="10" />
                  </div>
                </div>
              </div>
              <div className="border border-zinc-200 bg-white p-6">
                <h3 className="font-bold text-sm uppercase mb-4">Schedule Info</h3>
                <div className="space-y-4">
                  <InputField label="Months Elapsed" field="months_elapsed" placeholder="12" />
                  <InputField label="Total Months" field="total_months" placeholder="24" />
                </div>
                <Button onClick={handleSubmit} disabled={loading} className="w-full bg-black text-white py-3 h-auto mt-6" data-testid="fin-generate-btn">
                  {loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Processing...</> : 'Generate Financial Summary'}
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="dashboard">
          {summaryResult ? (
            <div className="space-y-6">
              {/* Health & Top KPIs */}
              <div className="border-2 border-black brutal-shadow bg-white p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-bold font-['Chivo'] uppercase">Financial Health</h3>
                    <p className="text-xs text-zinc-500">Based on EV analysis metrics</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {ev && <HealthBadge status={ev.health_status} />}
                    <Button onClick={handleAIAnalysis} disabled={loading} variant="outline" className="border-2" data-testid="fin-ai-btn">
                      {loading ? <Loader2 size={14} className="animate-spin" /> : <Lightbulb size={14} />} AI Analysis
                    </Button>
                  </div>
                </div>

                {ev && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <EVGauge value={ev.cost_performance_index} label="CPI" target={1} />
                    <EVGauge value={ev.schedule_performance_index} label="SPI" target={1} />
                    <MetricCard label="Cost Variance" value={ev.cost_variance} format="money" color={ev.cost_variance >= 0 ? "text-emerald-600" : "text-red-600"} />
                    <MetricCard label="Schedule Variance" value={ev.schedule_variance} format="money" color={ev.schedule_variance >= 0 ? "text-emerald-600" : "text-red-600"} />
                  </div>
                )}
              </div>

              {/* EV Metrics */}
              {ev && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <MetricCard label="EAC" value={ev.estimate_at_completion} format="money" subtext="Est. at Completion" />
                  <MetricCard label="ETC" value={ev.estimate_to_complete} format="money" subtext="Est. to Complete" />
                  <MetricCard label="VAC" value={ev.variance_at_completion} format="money" color={ev.variance_at_completion >= 0 ? "text-emerald-600" : "text-red-600"} subtext="Variance at Completion" />
                  <MetricCard label="TCPI" value={ev.to_complete_performance_index} format="index" subtext="To Complete Perf. Idx" />
                </div>
              )}

              {/* Cash Flow */}
              {cs && (
                <div className="border border-zinc-200 bg-white brutal-shadow p-6">
                  <h3 className="font-bold font-['Chivo'] uppercase mb-4">Cash Flow Summary</h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <MetricCard label="Certified" value={cs.payments_certified} format="money" />
                    <MetricCard label="Received" value={cs.payments_received} format="money" />
                    <MetricCard label="Outstanding" value={cs.outstanding_payments} format="money" color={cs.outstanding_payments > 0 ? "text-amber-600" : "text-emerald-600"} />
                    <MetricCard label="Retention Held" value={cs.retention_held} format="money" />
                    <MetricCard label="Net Cash" value={cs.net_cash_position} format="money" color={cs.net_cash_position >= 0 ? "text-emerald-600" : "text-red-600"} />
                  </div>
                </div>
              )}

              {/* AI Analysis */}
              {aiAnalysis && (
                <div className="border-2 border-zinc-800 bg-zinc-900 text-white p-6">
                  <h3 className="text-xs font-bold uppercase text-zinc-400 mb-3">AI Financial Analysis</h3>
                  <p className="text-sm leading-relaxed">{aiAnalysis.analysis || aiAnalysis.summary || JSON.stringify(aiAnalysis)}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-16 text-zinc-400 border border-dashed border-zinc-300 bg-zinc-50">
              <BarChart3 size={48} className="mx-auto mb-4 opacity-30" />
              <p>Enter financial data and click "Generate Summary" to see dashboard</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="scurves">
          {summaryResult ? (
            <div className="space-y-6">
              <div className="border-2 border-black brutal-shadow bg-white p-6">
                <SCurveChart 
                  pv={parseFloat(form.planned_value) || 0}
                  ev={parseFloat(form.earned_value) || 0}
                  ac={parseFloat(form.actual_cost) || 0}
                  bac={parseFloat(form.budget_at_completion) || parseFloat(form.original_contract_value) || 0}
                  title="Earned Value S-Curve Analysis"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="border-2 border-[#0047AB] bg-blue-50 p-4 text-center">
                  <p className="text-xs uppercase text-[#0047AB] font-bold">Planned Value</p>
                  <p className="text-2xl font-black text-[#0047AB]">{fmt(parseFloat(form.planned_value))}</p>
                </div>
                <div className="border-2 border-emerald-500 bg-emerald-50 p-4 text-center">
                  <p className="text-xs uppercase text-emerald-700 font-bold">Earned Value</p>
                  <p className="text-2xl font-black text-emerald-600">{fmt(parseFloat(form.earned_value))}</p>
                </div>
                <div className="border-2 border-[#FF4F00] bg-orange-50 p-4 text-center">
                  <p className="text-xs uppercase text-[#FF4F00] font-bold">Actual Cost</p>
                  <p className="text-2xl font-black text-[#FF4F00]">{fmt(parseFloat(form.actual_cost))}</p>
                </div>
              </div>

              {ev && (
                <div className="border border-zinc-200 bg-white brutal-shadow p-6">
                  <h3 className="font-bold font-['Chivo'] uppercase mb-4">Performance Trend Indicators</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-3 text-center border border-zinc-200">
                      <p className="text-xs uppercase text-zinc-500 mb-1">Cost Performance</p>
                      <div className="flex items-center justify-center gap-1">
                        {ev.cost_performance_index >= 1 ? <TrendingUp size={18} className="text-emerald-500" /> : <TrendingDown size={18} className="text-red-500" />}
                        <span className={`text-lg font-black ${ev.cost_performance_index >= 1 ? 'text-emerald-600' : 'text-red-600'}`}>{ev.cost_performance_index?.toFixed(3)}</span>
                      </div>
                      <p className="text-[10px] text-zinc-400">{ev.cost_performance_index >= 1 ? 'Under Budget' : 'Over Budget'}</p>
                    </div>
                    <div className="p-3 text-center border border-zinc-200">
                      <p className="text-xs uppercase text-zinc-500 mb-1">Schedule Performance</p>
                      <div className="flex items-center justify-center gap-1">
                        {ev.schedule_performance_index >= 1 ? <TrendingUp size={18} className="text-emerald-500" /> : <TrendingDown size={18} className="text-red-500" />}
                        <span className={`text-lg font-black ${ev.schedule_performance_index >= 1 ? 'text-emerald-600' : 'text-red-600'}`}>{ev.schedule_performance_index?.toFixed(3)}</span>
                      </div>
                      <p className="text-[10px] text-zinc-400">{ev.schedule_performance_index >= 1 ? 'Ahead' : 'Behind Schedule'}</p>
                    </div>
                    <div className="p-3 text-center border border-zinc-200">
                      <p className="text-xs uppercase text-zinc-500 mb-1">% Complete (Cost)</p>
                      <span className="text-lg font-black">{ev.percent_complete_cost?.toFixed(1)}%</span>
                    </div>
                    <div className="p-3 text-center border border-zinc-200">
                      <p className="text-xs uppercase text-zinc-500 mb-1">% Complete (Schedule)</p>
                      <span className="text-lg font-black">{ev.percent_complete_schedule?.toFixed(1)}%</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-16 text-zinc-400 border border-dashed border-zinc-300 bg-zinc-50">
              <Activity size={48} className="mx-auto mb-4 opacity-30" />
              <p>Generate financial summary first to see S-Curves</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="forecasting">
          {fc ? (
            <div className="space-y-6">
              <div className="border-2 border-black brutal-shadow bg-white p-6">
                <h3 className="font-bold font-['Chivo'] uppercase mb-4">Three-Point Cost Forecasting</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-5 bg-emerald-50 border-2 border-emerald-400 text-center">
                    <p className="text-xs uppercase font-bold text-emerald-700">Optimistic (EAC-O)</p>
                    <p className="text-3xl font-black text-emerald-600">{fmt(fc.optimistic_eac)}</p>
                    <p className="text-xs text-zinc-500 mt-1">Best case scenario</p>
                  </div>
                  <div className="p-5 bg-blue-50 border-2 border-[#0047AB] text-center">
                    <p className="text-xs uppercase font-bold text-[#0047AB]">Most Likely (EAC-ML)</p>
                    <p className="text-3xl font-black text-[#0047AB]">{fmt(fc.most_likely_eac)}</p>
                    <p className="text-xs text-zinc-500 mt-1">Expected outcome</p>
                  </div>
                  <div className="p-5 bg-red-50 border-2 border-red-400 text-center">
                    <p className="text-xs uppercase font-bold text-red-700">Pessimistic (EAC-P)</p>
                    <p className="text-3xl font-black text-red-600">{fmt(fc.pessimistic_eac)}</p>
                    <p className="text-xs text-zinc-500 mt-1">Worst case scenario</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <MetricCard label="Budget at Completion" value={parseFloat(form.budget_at_completion) || parseFloat(form.original_contract_value)} format="money" />
                <MetricCard label="EAC (CPI-based)" value={ev?.estimate_at_completion} format="money" />
                <MetricCard label="Variance at Completion" value={ev?.variance_at_completion} format="money" color={ev?.variance_at_completion >= 0 ? "text-emerald-600" : "text-red-600"} />
                <MetricCard label="To Complete (ETC)" value={ev?.estimate_to_complete} format="money" />
              </div>

              {fc.schedule_forecast && (
                <div className="border border-zinc-200 bg-white brutal-shadow p-6">
                  <h3 className="font-bold font-['Chivo'] uppercase mb-4">Schedule Forecast</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 bg-zinc-50 text-center">
                      <p className="text-xs uppercase text-zinc-500">Planned Duration</p>
                      <p className="text-xl font-black">{fc.schedule_forecast.planned_duration}mo</p>
                    </div>
                    <div className="p-4 bg-zinc-50 text-center">
                      <p className="text-xs uppercase text-zinc-500">Estimated Duration</p>
                      <p className={`text-xl font-black ${fc.schedule_forecast.estimated_duration > fc.schedule_forecast.planned_duration ? 'text-red-600' : 'text-emerald-600'}`}>{fc.schedule_forecast.estimated_duration}mo</p>
                    </div>
                    <div className="p-4 bg-zinc-50 text-center">
                      <p className="text-xs uppercase text-zinc-500">Variance</p>
                      <p className={`text-xl font-black ${fc.schedule_forecast.schedule_variance_months > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{fc.schedule_forecast.schedule_variance_months > 0 ? '+' : ''}{fc.schedule_forecast.schedule_variance_months}mo</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-16 text-zinc-400 border border-dashed border-zinc-300 bg-zinc-50">
              <TrendingUp size={48} className="mx-auto mb-4 opacity-30" />
              <p>Generate financial summary first to see forecasting</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FinancialSummaryView;
